import { computed, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type {
  EventEngagement,
  InterestRung,
  InterestSource,
} from '@@/types/api';
import type { InterestControlDescriptor } from '~/utils/event-policy';
import {
  almanacInterestControlFor,
  ideaInterestControlFor,
} from '~/utils/event-policy';
import { useInterestActions } from '~/composables/use-interest-actions';
import { useMyInterests } from '~/stores/use-my-interests';
import { useAuthStore } from '~/stores/use-auth-store';

interface UseCardInterestOptions {
  source: InterestSource;
  /** Stable per-card event id (native events are v4, Almanac ids are v7). */
  eventId: Ref<string> | string;
  /** Seed values from the card resource — kept optimistic, never refetched. */
  initialInterested: number;
  initialGoing: number;
  /** Optional metadata for the optimistic store upsert. */
  title?: string;
  kind?: string;
  /**
   * Almanac only: whether this event has concluded. A concluded Almanac card
   * renders no interest control (matching the detail page). Defaults to false.
   */
  isConcluded?: boolean;
}

export interface UseCardInterestReturn {
  descriptor: ComputedRef<InterestControlDescriptor>;
  myRung: Ref<InterestRung | null>;
  interestedCount: Ref<number>;
  goingCount: Ref<number>;
  isPending: Ref<boolean>;
  snackbar: Ref<boolean>;
  snackbarText: Ref<string>;
  snackbarColor: Ref<string>;
  setInterest: (rung: InterestRung | null) => Promise<void>;
}

/**
 * Per-card interest wiring for browse / search / discover event cards. Keeps the
 * card components thin: each card owns writable `myRung`/count refs, an interest
 * `descriptor`, and a `setInterest` action.
 *
 * Cards seed their button state from the shared interest STORE
 * (`rungByEventId`) rather than the card resource, because Almanac cards carry
 * no `my_rung` and the public (anonymous) browse fetch omits it for native too.
 * Unknown is always treated as "not interested" (null) — a card never renders a
 * filled state from an undefined resource field.
 *
 * SSR-safe: the store is inert on the server, so `myRung` is null during SSR and
 * hydrates client-side once the signed-in viewer's interests load.
 */
export function useCardInterest(
  options: UseCardInterestOptions,
): UseCardInterestReturn {
  const { source } = options;
  const authStore = useAuthStore();
  const myInterests = useMyInterests();

  const eventId = computed(() =>
    typeof options.eventId === 'string'
      ? options.eventId
      : options.eventId.value,
  );

  // Seed the current rung from the shared store, re-syncing when it loads or
  // after an optimistic `localSet`. `{ immediate: true }` seeds synchronously.
  const myRung = ref<InterestRung | null>(null);
  watch(
    () => myInterests.rungByEventId.get(eventId.value) ?? null,
    (rung) => {
      myRung.value = rung;
    },
    { immediate: true },
  );

  // Counts stay optimistic — cards can't reconcile authoritative counts from the
  // bare mutation response, so `refresh` is intentionally omitted below.
  const interestedCount = ref(options.initialInterested);
  const goingCount = ref(options.initialGoing);

  const descriptor = computed<InterestControlDescriptor>(() => {
    if (source === 'almanac') {
      // A concluded Almanac event renders no interest control (defense in depth
      // alongside the feed's today-floor); the caller derives `isConcluded` from
      // the card's timing via `isInformationalEventConcluded`.
      return almanacInterestControlFor(
        options.isConcluded ?? false,
        authStore.isAuthenticated,
        myRung.value,
      );
    }

    if (source === 'idea') {
      // Ideas are Interested-only and always live — no commitment/going
      // suppression, no concluded state.
      return ideaInterestControlFor(authStore.isAuthenticated, myRung.value);
    }

    // Native cards are Interested-only and never offer "Going" as a toggle. When
    // the shared store reports an effective rung of 'going', the viewer holds a
    // commitment (is effectively Going) — suppress the interest control entirely
    // to match the detail page, which shows the backer status card rather than an
    // interest toggle. The store resolves client-side post-auth, so a committed
    // viewer briefly sees the toggle during SSR/initial paint and it hides once
    // the store loads (same post-hydration seeding the control already relies on).
    if (!authStore.isAuthenticated) {
      return {
        source: 'native',
        visible: true,
        rung: null,
        options: ['interested'],
        action: 'login',
        disabled: false,
        disabledReason: null,
      };
    }
    if (myRung.value === 'going') {
      return {
        source: 'native',
        visible: false,
        rung: 'going',
        options: ['interested'],
        action: 'none',
        disabled: false,
        disabledReason: null,
      };
    }
    return {
      source: 'native',
      visible: true,
      rung: myRung.value,
      options: ['interested'],
      action: 'toggle',
      disabled: false,
      disabledReason: null,
    };
  });

  const { isPending, snackbar, snackbarText, snackbarColor, setInterest } =
    useInterestActions({
      source,
      eventId,
      myRung,
      interestedCount,
      goingCount,
      engagementMeta: () => {
        const meta: Partial<EventEngagement> = {};
        if (options.title !== undefined) meta.title = options.title;
        if (options.kind !== undefined) meta.kind = options.kind;
        return meta;
      },
      // No `refresh`: cards keep the optimistic counts.
    });

  return {
    descriptor,
    myRung,
    interestedCount,
    goingCount,
    isPending,
    snackbar,
    snackbarText,
    snackbarColor,
    setInterest,
  };
}
